/**
 * Nachspiel-Netz fuer den Durchreich-Weg beim Vorlesen. (21.09.2026, Ticket 2448)
 *
 * Der Server kann eine Soniox-Vertonung durchreichen, waehrend sie entsteht
 * (erster Ton nach ~0,5 s statt nach der ganzen Vertonung). Am 25.07. wurde
 * das abgeschaltet, weil zwei Dinge schiefgingen, die der SERVER nicht sehen
 * kann — beide sieht nur der Player:
 *
 *  1. Safari nahm den Strom ohne Laenge nicht an ("Media failed to decode").
 *  2. Ein Browser, der die Dauer nur schaetzt, hoerte zu frueh auf — Martin
 *     fehlten die letzten zwei Saetze.
 *
 * Darum liegt das Netz hier, im Player: `pufferUrl` fuehrt dieselbe Anfrage
 * ueber den gepufferten Weg (fuer Fall 1), `pruefeNachspiel` misst nach dem
 * Ende, ob der inzwischen fertige Zwischenspeicher laenger ist als das
 * Gespielte (Fall 2) — und liefert, ab wo der Rest zu spielen ist.
 *
 * Die Laenge kommt aus EINER Bereichs-Anfrage (bytes=0-0) auf den fertigen
 * Zwischenspeicher: der Server antwortet 206 mit `Content-Range: bytes
 * 0-0/<gesamt>`. Aus den Bytes folgt die Dauer, weil der Durchreich-Weg mit
 * fester Bitrate schreibt (libmp3lame -b:a 192k, siehe streamSonioxDirekt).
 */

/** Feste Bitrate des Durchreich-Wegs (server/tts-stream.ts, streamSonioxDirekt). */
const MP3_KBPS = 192;
/** Unter dieser Differenz gilt das Stueck als vollstaendig gespielt. */
export const TOLERANZ_SEK = 1.5;

export interface Nachspiel {
  /** Ab dieser Sekunde fehlt etwas. */
  ab: number;
  /** So viele Sekunden fehlen. */
  fehlt: number;
}

/**
 * Kommt dieses Stück über den Strom-Weg (/api/tts/stream)? (23.09.2026, Ticket 2448)
 *
 * Nur dort gibt es etwas nachzuladen und nachzumessen. Eine `blob:`-Adresse
 * ist eine fertige Datei — an sie `?puffer=1` zu hängen ergab eine Adresse,
 * die es nicht gibt, und nach JEDEM Stück eine sinnlose Nachfrage.
 */
export function istStromAdresse(url: string): boolean {
  return /\/api\/tts\/stream\?/.test(url);
}

/** Dieselbe Anfrage, aber ueber den gepufferten Weg (der Server liest `puffer=1`). */
export function pufferUrl(url: string): string {
  if (/[?&]puffer=1(?:&|#|$)/.test(url)) return url;
  const [ohneAnker, anker] = url.split("#", 2);
  const mitPuffer = `${ohneAnker}${ohneAnker.includes("?") ? "&" : "?"}puffer=1`;
  return anker ? `${mitPuffer}#${anker}` : mitPuffer;
}

/** Gesamtlaenge aus `Content-Range: bytes 0-0/12345`; null, wenn unbekannt. */
export function laengeAusContentRange(kopf: string | null | undefined): number | null {
  const m = /bytes\s+\d+-\d+\/(\d+)/.exec(kopf ?? "");
  return m ? Number(m[1]) : null;
}

export function sekundenAusBytes(bytes: number, kbps: number = MP3_KBPS): number {
  return (bytes * 8) / (kbps * 1000);
}

/** Fehlt nach `gespieltSek` noch mehr als die Toleranz? Dann: ab wo, wie viel. */
export function fehlenderRest(gespieltSek: number, bytes: number): Nachspiel | null {
  if (!(gespieltSek >= 0) || !(bytes > 0)) return null;
  const fehlt = sekundenAusBytes(bytes) - gespieltSek;
  return fehlt > TOLERANZ_SEK ? { ab: gespieltSek, fehlt } : null;
}

/**
 * Nach dem Ende eines durchgereichten Stuecks: ist der Zwischenspeicher
 * laenger als das Gespielte? Wirft nie — ein Netz, das reisst, ist keins.
 */
export async function pruefeNachspiel(
  url: string,
  gespieltSek: number,
  holen: typeof fetch = fetch,
): Promise<Nachspiel | null> {
  try {
    const r = await holen(pufferUrl(url), {
      headers: { Range: "bytes=0-0" },
      credentials: "include",
    });
    if (r.status !== 206) return null;
    const bytes = laengeAusContentRange(r.headers.get("Content-Range"));
    return bytes === null ? null : fehlenderRest(gespieltSek, bytes);
  } catch {
    return null;
  }
}
